import { useEffect, useState } from "react";

import { Box } from "@/components/Box/Box";
import { DotStack } from "@/components/DotStack/DotStack";
import { IconButton } from "@/components/IconButton/IconButton";
import { Stack } from "@/components/Stack/Stack";
import { SVGChevronLeft } from "@/components/SVGIcon/static/SVGChevronLeft";
import { SVGChevronRight } from "@/components/SVGIcon/static/SVGChevronRight";
import { Typography } from "@/components/Typography/Typography";

import { ItemSwitcherProps } from "./ItemSwitcher.types";

/**
 * ItemSwitcher component for navigating through items in a carousel or slideshow.
 *
 * This component provides left and right buttons to scroll through items, with optional indicators and page numbers.
 * It supports loop control to disable scrolling beyond the first or last item when loop control is off.
 *
 * Example usage of ItemSwitcher component
 * <ItemSwitcher
 *   selected={2}
 *   variant: "numbers",
 *   totalItems: 5,
 *   loopControl={true}
 *   onChange={(newSelected) => {
 *     console.log(`New selected item index: ${newSelected}`);
 *   }}
 * />
 *
 */
export const ItemSwitcher = /*#__PURE__*/ (function () {
  const ItemSwitcher: React.FC<ItemSwitcherProps> = ({
    loopControl = false,
    onChange,
    onLeftClick,
    onRightClick,
    leftAriaLabel,
    rightAriaLabel,
    buttonVariant = "default",
    ...rest
  }) => {
    // this is to support SSR to ensure there is element placeholder on first rendering
    const [isDisabled, setIsDisabled] = useState(true);
    useEffect(() => {
      setIsDisabled(false);
    }, []);

    return (
      <Stack spacing="xs">
        <IconButton
          icon={<SVGChevronLeft />}
          disabled={isDisabled || shouldDisable({ loopControl, atEdge: rest.selected === 1 })}
          aria-label={leftAriaLabel}
          variant={buttonVariant}
          onClick={() => {
            // Order onChange --> onLeftClick is important here as
            // consumer may want to override default behavior when jumping to next item e.g. Carousel with multiple items
            onChange?.(
              loopControl && rest.selected === 1 ? rest.totalItems : Math.max(1, rest.selected - 1)
            );
            onLeftClick?.();
          }}
        />
        <Indicators {...rest} />

        <IconButton
          icon={<SVGChevronRight />}
          disabled={
            isDisabled ||
            shouldDisable({
              loopControl,
              atEdge: rest.selected === rest.totalItems,
            })
          }
          aria-label={rightAriaLabel}
          variant={buttonVariant}
          onClick={() => {
            onChange?.(
              loopControl && rest.selected === rest.totalItems
                ? 1
                : Math.min(rest.selected + 1, rest.totalItems)
            );
            onRightClick?.();
          }}
        />
      </Stack>
    );
  };
  return ItemSwitcher;
})();

interface ShouldDisableProps {
  loopControl: boolean;
  atEdge: boolean;
}
const shouldDisable = ({ loopControl, atEdge }: ShouldDisableProps) => !loopControl && atEdge;

const Indicators: React.FC<
  Omit<ItemSwitcherProps, "showIndicators" | "loopControl" | "onChange">
> = ({ selected, variant, totalItems }) =>
  !variant || variant === "simple" ? null : (
    <Stack spacing="xs" alignItems="center">
      {variant === "numbers" && (
        <Box px="xs">
          <Typography variant="s2">
            {selected} / {totalItems}
          </Typography>
        </Box>
      )}
      {variant === "dots" && (
        <DotStack dotCount={totalItems} selectedDot={selected} hasPadding={false} />
      )}
    </Stack>
  );
